/* eslint-disable multiline-ternary */
import {
  Box,
  Grid,
  LinearProgress,
  Skeleton,
  Typography
} from '@mui/material'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useFormFactor } from '../../../hooks'
import {
  LikeButton,
  ResumePlaylistButton,
  RevisitPlaylistButton,
  StartPlaylistButton
} from '../../action-buttons'
import EmptyImgPlaylist from '../../icons/empty-img-playlist.svg'
import { PlaylistProgressContext } from '../../playlist-progress/context'
import GeneralCard from '../general-card'
import { useStyles } from './style'

const usePlaylistProgressSafe = () => {
  const context = React.useContext(PlaylistProgressContext)
  return context || { getProgress: () => 0, playlistProgressData: [] }
}

const PlaylistCard = (props) => {
  const { content, onSelect, onLike, loadingLike, className = '', currentTab, enrichedProgress, enrichedRead } = props

  const playlistItemCount = content?.content?.length || 0
  const { isDesktop } = useFormFactor()
  const classes = useStyles({ ...props, isDesktop })

  const { getProgress, playlistProgressData } = usePlaylistProgressSafe()

  const [progress, setProgress] = useState(0)

  useEffect(() => {
    if (enrichedProgress != null) {
      setProgress(Math.round(enrichedProgress))
      return
    }
    if (!playlistProgressData.length || !content) return
    const percent = getProgress(content?.pk)
    setProgress(percent)
  }, [enrichedProgress, playlistProgressData, getProgress, content])

  const cardContent = content && {
    ...content,
    primaryType: ['playlist'],
    subtitle: { html: content?.description }
  }

  const hasProgressData = enrichedProgress != null || playlistProgressData.length > 0
  const normalizedProgress = Math.min(Math.max(Math.round(progress || 0), 0), 100)
  const statusLabel = normalizedProgress >= 100
    ? 'Completed'
    : normalizedProgress > 0
      ? 'In Progress'
      : 'Not Started'

  const renderActionButton = () => {
    if (normalizedProgress >= 100) {
      return <RevisitPlaylistButton size='xs' onClick={onSelect} />
    }
    if (normalizedProgress > 0) {
      return <ResumePlaylistButton size='xs' onClick={onSelect} />
    }
    return <StartPlaylistButton size='xs' onClick={onSelect} />
  }

  const statusClass = normalizedProgress >= 100
    ? classes.statusCompleted
    : normalizedProgress > 0
      ? classes.statusInProgress
      : classes.statusNotStarted

  const actions = {
    leftActions: hasProgressData ? (
      <>
        <Grid item>
          {renderActionButton()}
        </Grid>
        <Grid item>
          <LikeButton
            likes={content?.likeCount ?? 0}
            hasLiked={content?.hasLiked}
            onClick={() => onLike(content.pk)}
            loading={loadingLike}
            size='xs'
            aria-label='Like this item'
          />
        </Grid>
        <Grid item>
          <Box className={statusClass}>
            <Typography variant='body2'>{statusLabel}</Typography>
          </Box>
        </Grid>
      </>
    ) : (
      <Skeleton variant='rect' width='100px' height='30px' />
    )
  }

  const readCount = Array.isArray(enrichedRead) ? enrichedRead.length : 0

  return (
    <GeneralCard
      content={cardContent}
      actions={actions}
      onSelect={onSelect}
      stack={2}
      expand
      defaultImage={EmptyImgPlaylist}
      componentProps={{ title: 'h2' }}
      className={clsx(classes.root, className)}
      classes={{
        root: classes.root,
        card: classes.card,
        title: classes.title,
        image: classes.image,
        summary: classes.summary,
        time: classes.time
      }}
      overrides={{
        actions: classes.actionsGrid
      }}
      playlistItemCount={playlistItemCount}
      isPlaylist
      currentTab={currentTab}
    >
      {content && hasProgressData ? (
        <Box display='flex' alignItems='center' mt={2} mb={-1}>
          <Box width='100%' mr={1}>
            <LinearProgress
              variant='determinate'
              value={normalizedProgress}
              aria-label={`playlist progress ${normalizedProgress}%`}
              classes={{
                root: classes.progress,
                bar: classes.foreground,
                colorPrimary: classes.background
              }}
            />
          </Box>
          <Box mt={2} minWidth={80}>
            <Typography variant='body2' color='textSecondary' noWrap>
              {`${readCount}/${playlistItemCount} · ${normalizedProgress}%`}
            </Typography>
          </Box>
        </Box>
      ) : null}
    </GeneralCard>
  )
}
PlaylistCard.propTypes = {
  /** Content to be displayed on card */
  content: PropTypes.object,
  /** The user's current progress through the playlist */
  progress: PropTypes.number,
  /** Callback function fired when the card is selected  */
  onSelect: PropTypes.func
}

export default PlaylistCard
